import { flushSync } from "react-dom";

import rethrowIfNotAborted from "apollo/rethrowIfNotAborted";

type ToastQueueAction = "add" | "remove" | "clear";

/**
 * View transitions are skipped (InvalidStateError) when the document is hidden.
 * Fall back to a plain update so toast dismissals still work in background tabs.
 */
function canUseViewTransition(): boolean {
  return (
    typeof document !== "undefined" &&
    "startViewTransition" in document &&
    document.visibilityState === "visible"
  );
}

/**
 * Wraps @react-stately/toast queue notifications so removals run inside the View Transitions API.
 * That preserves the old frame long enough for an exit animation without a second "ghost" mount.
 * `reducedMotion` should match {@link useReducedMotion} (app setting + system preference).
 * @see https://github.com/adobe/react-spectrum/pull/7631
 */
export function wrapToastQueueUpdate(
  fn: () => void,
  action: ToastQueueAction,
  reducedMotion: boolean
): void {
  if (action !== "remove" && action !== "clear") {
    fn();
    return;
  }

  if (reducedMotion) {
    fn();
    return;
  }

  if (!canUseViewTransition()) {
    fn();
    return;
  }

  document
    .startViewTransition(() => {
      flushSync(fn);
    })
    .ready.catch(rethrowIfNotAborted);
}
